import { themes } from '@design/design-tokens/themes/themes';
import fontsCss from '@static/fonts/fonts.css?raw';
import baseCss from '@static/styles/base.css?raw';
import resetCss from '@static/styles/reset.css?raw';
import variablesCss from '@static/styles/variables.css?raw';
import { config } from '@terminal/config';
import { getBrandedPdfContent } from '@terminal/utils/pdf-branding/pdf-branding';

import { PDF_FONT_FAMILY } from '../../alfred/research/utils/htmlNodes';

import { extractStylesheetRules } from './fetchStylesUtils';
import {
  transferCanvasesToClone,
  withExpandedDrawerAccordions,
} from './prepareDrawerContent';
import { embedFlagIcons } from './rasterizeClipboardImage';

interface HandleDownloadDrawerArgs {
  contentToPrint: HTMLDivElement;
  id: string | null;
  expandAccordions?: (expand: boolean) => void;
  title: string;
}

export const handleDownloadDrawer = async ({
  contentToPrint,
  id,
  expandAccordions,
  title,
}: HandleDownloadDrawerArgs): Promise<void> => {
  await withExpandedDrawerAccordions({
    expandAccordions,
    run: async () => {
      const tempContainer = document.createElement('div');
      tempContainer.innerHTML = contentToPrint.innerHTML;
      tempContainer.style.position = 'fixed';
      tempContainer.style.left = '-99999px';
      tempContainer.style.top = '0';
      tempContainer.style.display = 'block';
      tempContainer.style.width = `${
        contentToPrint.offsetWidth ||
        contentToPrint.parentElement?.clientWidth ||
        0
      }px`;
      tempContainer.style.pointerEvents = 'none';
      tempContainer.setAttribute('aria-hidden', 'true');
      document.body.appendChild(tempContainer);
      transferCanvasesToClone(contentToPrint, tempContainer);

      let htmlContent: string;
      try {
        const accordionWrappers = tempContainer.querySelectorAll(
          '[data-testid="transition-wrapper"]'
        );
        accordionWrappers.forEach(wrapper => {
          if (
            wrapper instanceof HTMLElement &&
            wrapper.style.height !== '0px'
          ) {
            wrapper.style.height = 'auto';
          }
        });

        const logoImages = tempContainer.querySelectorAll(
          'img[alt="logo-image"]'
        );
        logoImages.forEach(img => {
          if (img instanceof HTMLImageElement && img.src) {
            img.src = img.src.replace(/\/light\.png$/, '/dark.png');
          }
        });

        await embedFlagIcons(tempContainer);

        htmlContent = getBrandedPdfContent(
          config.urls.root,
          tempContainer.innerHTML
        );
      } finally {
        tempContainer.remove();
      }

      const lightThemeCss = Object.entries(themes.light_default)
        .map(([key, value]) => `${key}: ${value};`)
        .join('\n          ');

      const browserStylesheetStyles = (
        await Promise.all(
          Array.from(document.styleSheets).map(stylesheet =>
            extractStylesheetRules(stylesheet)
          )
        )
      )
        .filter(Boolean)
        .join('\n');

      const pdfTitle = `${title} - ${id || 'Report'}`;

      const printDocument = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <title> ${pdfTitle}</title>
            <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/flag-icons/css/flag-icons.min.css">
            <style>
              * {
                print-color-adjust: exact;
                -webkit-print-color-adjust: exact;
              }

              body {
                font-family: ${PDF_FONT_FAMILY};
              }

              /* Static CSS files */
              ${resetCss}
              ${variablesCss}
              ${baseCss}
              ${fontsCss}
              
              /* Stylesheets extracted from browser's stylesheet API (same-origin via cssRules, cross-origin via fetch) */
              ${browserStylesheetStyles}
                            
              :root {
                ${lightThemeCss}
              }

              @media print {
                [data-print-scrollable] {
                  max-height: none;
                  overflow: visible;
                  height: auto;
                }

              }
            </style>
          </head>
          <body>
            <div>
              ${htmlContent}
            </div>
          </body>
        </html>
      `;

      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);

      const iframeDoc =
        iframe.contentDocument || iframe.contentWindow?.document;

      if (iframeDoc) {
        const blob = new Blob([printDocument], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        iframe.src = url;

        iframe.onload = async () => {
          const originalTitle = document.title;
          document.title = pdfTitle;

          await iframeDoc.fonts?.ready;

          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();

          iframe.contentWindow?.addEventListener('afterprint', () => {
            document.title = originalTitle;
            URL.revokeObjectURL(url);
            document.body.removeChild(iframe);
          });
        };
      }
    },
  });
};
